import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import roundTo from 'round-to';
import moment from 'moment';

import { getFilterQueryParams, getHttpHeader } from '../../../../util/FetchTools';

import useDataFetch from '../../../../hooks/useDataFetch';

import {
   CenteredMsgShowing,
   ChartWrapper,
   HeightSetContainer,
   RemoveBottomMargin,
   RightSide,
   SetChartHeight,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   TwoSideRow,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
} from '../../../../styledComponents/styledComponents';

import { historicalUsageBarChartOptions } from '../../../../constants/chartjs';

import { Bar } from 'react-chartjs-2';
import { setAlarm } from '../../../../redux/actions/general';
import { CsvExportFromApiButton, Spinner } from '../../../common';

import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities';
import { colorToRgba } from '../../../../util/UtilFunctions';

import { CardTitle, Col, Row } from 'reactstrap';

import numeral from 'numeral';
import BootstrapTable from 'react-bootstrap-table-next';
import { showOverflowTooltip } from '../../../common/OverflowTooltip/OverflowTooltip';

import {
   cancelTokenSelector,
   countriesSelector,
   endTimespanSelector,
   hashSelector,
   intervalTimespanSelector,
   settingsSelector,
   startTimespanSelector,
   streamNamesSelector,
   timespanSelector,
   titleTimespanSelector,
} from '../../../../redux/selectors/selectors';

import { PropTypes } from 'prop-types';
import styled from 'styled-components';

import 'spinkit/css/spinkit.css';

import { HISTORY_VIEW } from '../../../../constants/general';
import ComponentTools from '../../../../util/ComponentTools';
import { roundToGb } from '../../../../util/NumberFormatter';
import ErrorDisplay from '../../misuse/ErrorDisplay';

const VariantHeightRow = styled(Row)`
   margin-bottom: 30px;
   @media (min-width: 1200px) {
      margin-bottom: 0px;
   }
`;

const VariantTopMargin = styled.div`
   margin-top: -32px;
   @media (min-width: 1200px) {
      margin-top: 0px;
   }
`;

const chartOptions = historicalUsageBarChartOptions();

const blue = getStyle('--info');
const red = getStyle('--danger');

const protocolNameLengthPx = 50;

// Usage data is aggregated with a delay. The most recent month only becomes fully aggregated once
// its end lies at least this many hours in the past; until then the backend omits it and we show a
// hint instead of presenting an incomplete month as if it were complete.
const DATA_AGGREGATION_DELAY_HOURS = 49;

const USAGE_SETTINGS = {
   bytes: {
      unit: 'GB',
      chartLabelPlayout: 'Playout [GB]',
      chartLabelIngest: 'Ingest [GB]',
      unitConversion: (value) => roundTo(value / 1e9, 3),
      resUnitPropNames: 'bytes',
   },
   playtime: {
      unit: 'h',
      chartLabelPlayout: 'Playout [h]',
      chartLabelIngest: 'Ingest [h]',
      unitConversion: (value) => roundTo(value / 3600, 0),
      resUnitPropNames: 'seconds',
   },
};

function getTableColumns(usageSwitch, interval) {
   return [
      {
         text: interval === 'year' ? 'Year' : 'Month',
         dataField: 'label',
         style: { verticalAlign: 'middle', textAlign: 'center' },
         headerAlign: 'center',
         formatter: (cell) => showOverflowTooltip(cell),
      },
      {
         text: `Playout [${USAGE_SETTINGS[usageSwitch].unit}]`,
         dataField: 'playout',
         style: { verticalAlign: 'middle', textAlign: 'center' },
         headerAlign: 'center',
         formatter: (cell) => numeral(USAGE_SETTINGS[usageSwitch].unitConversion(cell)).format('0,0.000'),
      },
      {
         text: `Ingest [${USAGE_SETTINGS[usageSwitch].unit}]`,
         dataField: 'ingest',
         style: { verticalAlign: 'middle', textAlign: 'center' },
         headerAlign: 'center',
         formatter: (cell) => numeral(USAGE_SETTINGS[usageSwitch].unitConversion(cell)).format('0,0.000'),
      },
      {
         text: `Total [${USAGE_SETTINGS[usageSwitch].unit}]`,
         dataField: 'total',
         style: { verticalAlign: 'middle', textAlign: 'center' },
         headerAlign: 'center',
         formatter: (cell) => numeral(USAGE_SETTINGS[usageSwitch].unitConversion(cell)).format('0,0.000'),
      },
   ];
}

function getNewDataSets() {
   return [
      {
         label: 'Playout [GB]',
         backgroundColor: colorToRgba(red, 10),
         borderColor: red,
         borderWidth: 1,
         data: [],
         totalBytes: 0,
      },
      {
         label: 'Ingest [GB]',
         backgroundColor: colorToRgba(blue, 10),
         borderColor: blue,
         borderWidth: 1,
         data: [],
         totalBytes: 0,
      },
   ];
}

function createChartData(data, timespan, settings) {
   const usage = data.result;

   const updatedChartData = {
      labels: [],
      datasets: getNewDataSets(),
   };

   const labels = [];

   ComponentTools.setBarChartLabels(updatedChartData, labels, timespan, settings.weekType);

   const now = Date.now();

   labels.forEach((label) => {
      const start = label.start;
      const end = label.end;
      if (start < now) {
         const usageInterval = usage.find((bucket) => bucket.key >= start && bucket.key < end);

         if (usageInterval !== undefined) {
            // chart bars are rounded per interval, totals accumulate the raw bytes
            // and are converted/rounded once at display time
            updatedChartData.datasets[0].data.push(roundTo(usageInterval.playout / 1e9, 3));
            updatedChartData.datasets[0].totalBytes += usageInterval.playout;

            updatedChartData.datasets[1].data.push(roundTo(usageInterval.ingest / 1e9, 3));
            updatedChartData.datasets[1].totalBytes += usageInterval.ingest;
         } else {
            updatedChartData.datasets[0].data.push(0);
            updatedChartData.datasets[1].data.push(0);
         }
      }
   });

   return updatedChartData;
}

function createTableData(data, interval) {
   const usage = data.result;
   const tableData = [];

   for (let index = 0; index < usage.length; index++) {
      const dataObj = usage[index];
      const label = moment.utc(dataObj.key).format(interval === 'year' ? 'YYYY' : 'MMMM YYYY');
      const playout = dataObj.playout;
      const ingest = dataObj.ingest;
      const total = playout + ingest;
      tableData.push({ label, playout, ingest, total });
   }

   return tableData;
}
function Usage({ height }) {
   const [chart, setChart] = useState({
      labels: [],
      datasets: getNewDataSets(),
   });

   const [tableData, setTableData] = useState([]);

   const [noData, setNoData] = useState(false);

   const hash = useSelector(hashSelector);
   const countries = useSelector(countriesSelector);
   const streamNames = useSelector(streamNamesSelector);

   const start = useSelector(startTimespanSelector);
   const end = useSelector(endTimespanSelector);
   const interval = useSelector(intervalTimespanSelector);
   const timespanTitle = useSelector(titleTimespanSelector);

   const timespan = useSelector(timespanSelector);
   const cancelToken = useSelector(cancelTokenSelector);
   const settings = useSelector(settingsSelector);
   const dispatch = useDispatch();

   const header = useMemo(() => getHttpHeader(hash), [hash]);
   const urlFilterQuery = getFilterQueryParams(countries, [], [], streamNames, HISTORY_VIEW);
   const url = `/v2/usage/history/timeseries?from=${start.format()}&to=${end.format()}&interval=${interval}${urlFilterQuery}`;

   const { successful, isLoading, errorMessage, data } = useDataFetch(url, { header, cancelToken });
   useEffect(() => {
      if (successful) {
         setChart(createChartData(data, timespan, settings));
         setTableData(createTableData(data, interval));
         setNoData(data.result.reduce((sum, dataPoint) => (sum += dataPoint.playout + dataPoint.ingest), 0) === 0);
      }

      if (errorMessage) {
         dispatch(setAlarm('danger', errorMessage));
      }
   }, [data, successful, errorMessage, dispatch, timespan, settings, interval]);

   const playoutBytes = chart.datasets[0].totalBytes;
   const ingestBytes = chart.datasets[1].totalBytes;
   const bothBytes = playoutBytes + ingestBytes;

   const errorOrNoData = errorMessage || noData;

   // Usage is aggregated monthly with a delay: a month only becomes available once its end
   // lies at least DATA_AGGREGATION_DELAY_HOURS in the past. firstUnavailableMonthStart is the
   // start of the earliest month that is not yet available; this is also the exclusive cutoff
   // the range end must stay within (matching the date picker's start-of-month boundaries).
   // The hint is shown whenever the selected range reaches beyond it.
   const now = moment.utc();
   const firstUnavailableMonthStart = now.clone().subtract(DATA_AGGREGATION_DELAY_HOURS, 'hours').startOf('month');
   const availableUntilLabel = firstUnavailableMonthStart.clone().subtract(1, 'month').format('MMM YYYY');
   const showAggregationDelayHint = end.isAfter(firstUnavailableMonthStart);

   // With a yearly interval the current year is aggregated from completed months only. When the
   // aggregation delay hint is not shown but the requested range still reaches into the current
   // year, the current-year bucket is necessarily partial, so we point out that it only covers
   // months up to the last fully aggregated one.
   const showCurrentYearPartialHint =
      interval === 'year' && !showAggregationDelayHint && end.isAfter(now.clone().startOf('year'));
   // The range end is exclusive and aligned to a month start, so the last month actually included
   // in the data is the one before it.
   const lastIncludedMonthLabel = end.clone().subtract(1, 'month').format('MMM YYYY');

   return (
      <WidgetContainer>
         <WidgetHeaderContainer>
            <HeightSetContainer $heightInPx={23}>
               <TwoSideFlexContainer>
                  <SimpleFlexRow>
                     <SimpleFlexItem>
                        <CsvExportFromApiButton
                           url={url}
                           header={header}
                           cancelToken={cancelToken}
                           filename={'Historical_Usage'}
                        />
                     </SimpleFlexItem>
                  </SimpleFlexRow>
                  <SimpleFlexRow></SimpleFlexRow>
               </TwoSideFlexContainer>
            </HeightSetContainer>
         </WidgetHeaderContainer>
         <WidgetBodyContainer>
            <TwoSideRow marginRightPx={15}>
               <div>
                  <CardTitle className='mb-0'>Historical usage Playout/Ingest</CardTitle>
                  <div className='small text-muted'>{`${timespanTitle}\u00A0\u00A0\u00A0( UTC time )`}</div>
                  {showAggregationDelayHint && !errorMessage && (
                     <div className='small font-italic' style={{ color: 'rgba(115, 0, 0, 0.9)' }}>
                        {`Usage data is aggregated in monthly intervals and becomes available a few days after each
                        month ends — currently up to and including ${availableUntilLabel}.`}
                        {interval === 'year' &&
                           ` The current year is only shown when the selected range ends on or before ${availableUntilLabel}.`}
                     </div>
                  )}
                  {showCurrentYearPartialHint && !errorMessage && (
                     <div className='small font-italic' style={{ color: 'rgba(115, 0, 0, 0.9)' }}>
                        {`For the current year, usage is aggregated from completed months only and is shown up to and including the last full month in the selected range (${lastIncludedMonthLabel}).`}
                     </div>
                  )}
               </div>
               <RightSide minWidthPx={210}>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>Playout:</RightSide>
                     <div className='text-danger'>
                        {errorMessage || isLoading ? '0 GB' : `${roundToGb(playoutBytes)} GB`}
                     </div>
                  </TwoSideRow>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>Ingest:</RightSide>
                     <div className='text-info'>
                        {errorMessage || isLoading ? '0 GB' : `${roundToGb(ingestBytes)} GB`}
                     </div>
                  </TwoSideRow>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>Total:</RightSide>
                     <div>{errorMessage || isLoading ? '0 GB' : `${roundToGb(bothBytes)} GB`}</div>
                  </TwoSideRow>
               </RightSide>
            </TwoSideRow>
            <Row className='animated fadeIn'>
               <Col xs='12' sm='12' md='12' lg='12' xl='6'>
                  <VariantHeightRow>
                     <Col>
                        <ChartWrapper className='chart-wrapper' marginTopPx={14} height={height}>
                           <Spinner loading={isLoading} parentTopMarginPx={50}>
                              {errorOrNoData ? (
                                 <CenteredMsgShowing height={height}>
                                    {errorMessage ? (
                                       <ErrorDisplay error={errorMessage} msgColor={'rgb(80,80,80)'}></ErrorDisplay>
                                    ) : (
                                       'No data available'
                                    )}
                                 </CenteredMsgShowing>
                              ) : (
                                 <SetChartHeight height={height}>
                                    <Bar data={chart} options={chartOptions} />
                                 </SetChartHeight>
                              )}
                           </Spinner>
                        </ChartWrapper>
                     </Col>
                  </VariantHeightRow>
               </Col>
               <Col xs='12' sm='12' md='12' lg='12' xl='6'>
                  <Row className='pt-3'>
                     <Col>
                        <VariantTopMargin>
                           <ChartWrapper
                              className='chart-wrapper'
                              marginTopPx={5}
                              height={isLoading ? height - 10 : undefined}
                           >
                              <Spinner loading={isLoading} parentTopMarginPx={52}>
                                 {errorOrNoData ? (
                                    <CenteredMsgShowing height={height} marginTopPx={-7}>
                                       {errorMessage ? (
                                          <ErrorDisplay error={errorMessage} msgColor={'rgb(80,80,80)'}></ErrorDisplay>
                                       ) : (
                                          'No data available'
                                       )}
                                    </CenteredMsgShowing>
                                 ) : (
                                    <RemoveBottomMargin>
                                       <BootstrapTable
                                          id={'usage'}
                                          keyField='label'
                                          data={tableData}
                                          columns={getTableColumns('bytes', interval)}
                                          bootstrap4
                                          bordered={true}
                                          classes='fixed-table table-responsive-sm'
                                          striped
                                          hover
                                       />
                                    </RemoveBottomMargin>
                                 )}
                              </Spinner>
                           </ChartWrapper>
                        </VariantTopMargin>
                     </Col>
                  </Row>
               </Col>
            </Row>
         </WidgetBodyContainer>
      </WidgetContainer>
   );
}

Usage.propTypes = {
   height: PropTypes.number,
};

Usage.defaultProps = {
   height: 300,
};

export default Usage;
